<template>
  <b-card no-body class="executed-maintenances-card">
    <!-- Header -->
    <div class="card-header d-flex flex-wrap align-items-center justify-content-between">
      <div>
        <h5 class="mb-25 font-weight-bolder d-flex align-items-center">
          <feather-icon icon="CheckSquareIcon" size="18" class="text-primary mr-50" />
          {{ $t('Executed Maintenances') }}
          <b-badge v-if="totalRecords" variant="light-primary" pill class="ml-50">
            {{ totalRecords }}
          </b-badge>
        </h5>
        <small class="text-muted">
          {{ $t('History of maintenances registered for this asset') }}
        </small>
      </div>

      <div class="d-flex flex-wrap align-items-center mt-50 mt-md-0">
        <!-- Summary Badges -->
        <div class="d-none d-lg-flex align-items-center mr-1">
          <b-badge
            v-b-tooltip.hover
            :title="$t('Maintenances linked to a preventive plan')"
            variant="light-primary"
            class="mr-50 px-75 py-50"
          >
            <feather-icon icon="BookmarkIcon" size="12" class="mr-25" />
            {{ withPlanCount }} {{ $t('With Plan') }}
          </b-badge>
          <b-badge
            v-b-tooltip.hover
            :title="$t('Ad-hoc or corrective maintenances without a plan')"
            variant="light-secondary"
            class="px-75 py-50"
          >
            <feather-icon icon="ToolIcon" size="12" class="mr-25" />
            {{ withoutPlanCount }} {{ $t('Without Plan') }}
          </b-badge>
        </div>

        <b-button
          size="sm"
          variant="primary"
          :to="{ name: 'maintenance-record', query: { assetId } }"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          {{ $t('Register maintenance') }}
        </b-button>
      </div>
    </div>

    <!-- Filters & Search Toolbar -->
    <div class="card-body py-1 border-bottom bg-light-secondary-subtle">
      <b-row class="align-items-center">
        <!-- Plan Type Filter Chips -->
        <b-col cols="12" md="5" class="mb-75 mb-md-0">
          <div class="d-flex flex-wrap align-items-center">
            <span class="small text-muted font-weight-bold mr-50">{{ $t('Filter') }}:</span>
            <b-button-group size="sm">
              <b-button
                :variant="planFilter === 'all' ? 'primary' : 'outline-primary'"
                @click="planFilter = 'all'"
              >
                {{ $t('All') }} ({{ totalRecords }})
              </b-button>
              <b-button
                :variant="planFilter === 'with-plan' ? 'primary' : 'outline-primary'"
                @click="planFilter = 'with-plan'"
              >
                <feather-icon icon="BookmarkIcon" size="12" class="mr-25" />
                {{ $t('With Plan') }}
              </b-button>
              <b-button
                :variant="planFilter === 'without-plan' ? 'primary' : 'outline-primary'"
                @click="planFilter = 'without-plan'"
              >
                <feather-icon icon="ToolIcon" size="12" class="mr-25" />
                {{ $t('Without Plan') }}
              </b-button>
            </b-button-group>
          </div>
        </b-col>

        <!-- Status Filter & Search -->
        <b-col cols="12" md="7">
          <div class="d-flex flex-wrap align-items-center justify-content-md-end">
            <!-- Vue Select Status Filter -->
            <v-select
              v-model="statusFilter"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="statusOptions"
              :reduce="opt => opt.value"
              :clearable="false"
              class="status-select mr-50 mb-50 mb-sm-0 flex-grow-1 flex-md-grow-0"
              style="min-width: 170px;"
            />

            <div class="d-flex align-items-center flex-grow-1 flex-sm-grow-0" style="max-width: 260px;">
              <b-form-input
                v-model="searchQuery"
                size="sm"
                :placeholder="$t('Search by note, invoice, driver...')"
                class="mr-50"
              />
              <b-button
                v-b-tooltip.hover
                :title="$t('Refresh')"
                size="sm"
                variant="flat-secondary"
                class="btn-icon"
                :disabled="isLoading"
                @click="fetchRecords"
              >
                <feather-icon icon="RotateCwIcon" size="14" :class="{ 'spin-animation': isLoading }" />
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Body -->
    <b-card-body class="p-0">
      <!-- Loading State -->
      <div v-if="isLoading" class="text-center py-4">
        <b-spinner variant="primary" />
        <p class="text-muted small mt-1 mb-0">{{ $t('Loading executed maintenances...') }}</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredRecords.length === 0" class="text-center py-4 px-2">
        <div class="empty-icon-wrapper mb-1">
          <feather-icon icon="ClipboardIcon" size="36" class="text-muted" />
        </div>
        <h6 class="font-weight-bolder mb-50">{{ $t('No executed maintenances found') }}</h6>
        <p class="text-muted small mb-1 mx-auto" style="max-width: 380px;">
          {{ searchQuery || planFilter !== 'all' || statusFilter !== 'all'
            ? $t('No records match your selected filter criteria. Try clearing filters.')
            : $t('No maintenance has been registered for this asset yet.') }}
        </p>
        <b-button
          v-if="!searchQuery && planFilter === 'all' && statusFilter === 'all'"
          size="sm"
          variant="outline-primary"
          :to="{ name: 'maintenance-record', query: { assetId } }"
        >
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          {{ $t('Register first maintenance') }}
        </b-button>
        <b-button
          v-else
          size="sm"
          variant="outline-secondary"
          @click="resetFilters"
        >
          {{ $t('Clear filters') }}
        </b-button>
      </div>

      <!-- Table View -->
      <div v-else class="table-responsive">
        <b-table
          :items="filteredRecords"
          :fields="tableFields"
          responsive
          hover
          class="mb-0 executed-table align-middle"
          primary-key="id"
        >
          <!-- Column: Plan / Type -->
          <template #cell(planType)="data">
            <div class="d-flex flex-column align-items-start">
              <!-- WITH PLAN BADGE -->
              <div v-if="hasPlan(data.item)" class="plan-tag-box with-plan">
                <b-badge variant="light-primary" class="d-inline-flex align-items-center font-weight-bolder text-wrap text-left py-50 px-75">
                  <feather-icon icon="BookmarkIcon" size="13" class="mr-50 text-primary flex-shrink-0" />
                  <span>{{ data.item.preventiveMaintenanceName || $t('Preventive Plan') }}</span>
                </b-badge>
                <div class="tag-caption text-primary small mt-25 d-flex align-items-center">
                  <feather-icon icon="ShieldCheckIcon" size="11" class="mr-25" />
                  <span>{{ $t('With Maintenance Plan') }}</span>
                </div>
              </div>

              <!-- WITHOUT PLAN BADGE -->
              <div v-else class="plan-tag-box without-plan">
                <b-badge variant="light-secondary" class="d-inline-flex align-items-center font-weight-bold text-wrap text-left py-50 px-75">
                  <feather-icon icon="ToolIcon" size="13" class="mr-50 text-secondary flex-shrink-0" />
                  <span>{{ $t('Without Plan (Standalone)') }}</span>
                </b-badge>
                <div class="tag-caption text-muted small mt-25 d-flex align-items-center">
                  <feather-icon icon="InfoIcon" size="11" class="mr-25" />
                  <span>{{ $t('Corrective / Ad-hoc') }}</span>
                </div>
              </div>

              <div v-if="data.item.additionalCode" class="mt-25">
                <small class="text-muted font-weight-bold">
                  {{ $t('Code') }}: {{ data.item.additionalCode }}
                </small>
              </div>
            </div>
          </template>

          <!-- Column: Status -->
          <template #cell(status)="data">
            <div class="d-inline-block">
              <b-badge v-if="data.item.endDate" variant="light-success" class="d-inline-flex align-items-center font-weight-bold py-50 px-75">
                <feather-icon icon="CheckCircleIcon" size="13" class="mr-25" />
                <span>{{ $t('Finished') }}</span>
              </b-badge>
              <b-badge v-else variant="light-warning" class="d-inline-flex align-items-center font-weight-bold py-50 px-75">
                <feather-icon icon="ClockIcon" size="13" class="mr-25" />
                <span>{{ $t('In progress') }}</span>
              </b-badge>
            </div>
          </template>

          <!-- Column: Period / Dates -->
          <template #cell(dates)="data">
            <div>
              <div class="font-weight-bold text-dark d-flex align-items-center">
                <feather-icon icon="CalendarIcon" size="12" class="text-muted mr-25" />
                <span>{{ formatDate(data.item.startDate) }}</span>
              </div>
              <div v-if="data.item.endDate" class="text-muted small mt-25">
                {{ $t('Until') }}: {{ formatDate(data.item.endDate) }}
              </div>
              <div v-if="data.item.maintenanceDuration" class="mt-25">
                <b-badge variant="light-info" class="small px-50">
                  <feather-icon icon="ClockIcon" size="10" class="mr-25" />
                  {{ data.item.maintenanceDuration }}
                </b-badge>
              </div>
            </div>
          </template>

          <!-- Column: Metrics at Execution -->
          <template #cell(metrics)="data">
            <div>
              <div class="d-flex align-items-center text-nowrap mb-25" :class="{ 'text-muted': !data.item.hourmeter }">
                <feather-icon icon="ClockIcon" size="12" class="text-primary mr-25" />
                <span class="font-weight-600 mr-25">{{ formatMetric(data.item.hourmeter) }}</span>
                <small class="text-muted">h</small>
              </div>
              <div class="d-flex align-items-center text-nowrap" :class="{ 'text-muted': !data.item.odometer }">
                <feather-icon icon="NavigationIcon" size="12" class="text-primary mr-25" />
                <span class="font-weight-600 mr-25">{{ formatMetric(data.item.odometer) }}</span>
                <small class="text-muted">Km</small>
              </div>
            </div>
          </template>

          <!-- Column: Value / Cost -->
          <template #cell(value)="data">
            <div>
              <div class="font-weight-bolder text-dark">
                {{ formatCurrency(data.item.value) }}
              </div>
              <div v-if="data.item.items && data.item.items.length" class="mt-25">
                <b-badge variant="light-secondary" pill class="small font-weight-normal">
                  {{ data.item.items.length }} {{ data.item.items.length === 1 ? $t('item') : $t('items') }}
                </b-badge>
              </div>
            </div>
          </template>

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <div class="d-flex align-items-center justify-content-end">
              <!-- Expand row button if details exist -->
              <b-button
                v-if="hasDetails(data.item)"
                v-b-tooltip.hover.top="$t('Details')"
                variant="flat-secondary"
                size="sm"
                class="btn-icon mr-25"
                @click="data.toggleDetails"
              >
                <feather-icon :icon="data.detailsShowing ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
              </b-button>

              <!-- Edit/View button -->
              <b-button
                v-b-tooltip.hover.top="$t('Edit')"
                variant="flat-primary"
                size="sm"
                class="btn-icon"
                :to="{ name: 'maintenance-record', params: { id: data.item.id } }"
              >
                <feather-icon icon="EditIcon" size="16" />
              </b-button>
            </div>
          </template>

          <!-- Row Details: Notes & Sub-items & Driver/Invoice -->
          <template #row-details="row">
            <b-card no-body class="bg-light shadow-none border m-1">
              <b-card-body class="p-1">
                <!-- Driver / Invoice info if present -->
                <div v-if="row.item.driverName || row.item.invoice" class="mb-1 d-flex flex-wrap align-items-center pb-50 border-bottom">
                  <span v-if="row.item.driverName" class="mr-2 small font-weight-bold text-dark">
                    <feather-icon icon="UserIcon" size="12" class="text-muted mr-25" />
                    {{ $t('Responsible') }}: <span class="font-weight-normal text-body">{{ row.item.driverName }}</span>
                  </span>
                  <span v-if="row.item.invoice" class="small font-weight-bold text-dark">
                    <feather-icon icon="FileTextIcon" size="12" class="text-muted mr-25" />
                    NF: <span class="font-weight-normal text-body">{{ row.item.invoice }}</span>
                  </span>
                </div>

                <b-row>
                  <!-- Notes -->
                  <b-col v-if="row.item.note" cols="12" :md="row.item.items && row.item.items.length ? 5 : 12" class="mb-1 mb-md-0">
                    <h6 class="font-weight-bold text-muted small text-uppercase mb-50">
                      <feather-icon icon="FileTextIcon" size="12" class="mr-25" />
                      {{ $t('Notes') }}
                    </h6>
                    <p class="mb-0 text-body small bg-white p-75 rounded border">
                      {{ row.item.note }}
                    </p>
                  </b-col>

                  <!-- Sub Items -->
                  <b-col v-if="row.item.items && row.item.items.length" cols="12" :md="row.item.note ? 7 : 12">
                    <h6 class="font-weight-bold text-muted small text-uppercase mb-50">
                      <feather-icon icon="ListIcon" size="12" class="mr-25" />
                      {{ $t('Maintenance Items') }} ({{ row.item.items.length }})
                    </h6>
                    <div class="table-responsive bg-white rounded border">
                      <table class="table table-sm table-borderless mb-0 small">
                        <thead>
                          <tr class="border-bottom text-muted">
                            <th class="py-50">{{ $t('Description / Type') }}</th>
                            <th class="py-50 text-right">{{ $t('Value') }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(subItem, idx) in row.item.items" :key="idx" class="border-bottom-last-none">
                            <td class="py-50">{{ subItem.maintenanceTypeName || subItem.name || '—' }}</td>
                            <td class="py-50 text-right font-weight-bold">{{ formatCurrency(subItem.value) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </b-col>
                </b-row>
              </b-card-body>
            </b-card>
          </template>
        </b-table>

        <!-- Pagination Bar -->
        <div class="px-1 py-1 border-top">
          <b-row class="align-items-center">
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start mb-1 mb-sm-0">
              <span class="text-muted small">
                {{ $t('Showing') }} {{ dataMeta.from }} {{ $t('to') }} {{ dataMeta.to }} {{ $t('of') }} {{ totalRecords }} {{ $t('entries') }}
              </span>
            </b-col>
            <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
              <b-pagination
                v-model="currentPage"
                :total-rows="totalRecords"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon icon="ChevronLeftIcon" size="16" />
                </template>
                <template #next-text>
                  <feather-icon icon="ChevronRightIcon" size="16" />
                </template>
              </b-pagination>
            </b-col>
          </b-row>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCardBody,
  BCol,
  BFormInput,
  BPagination,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  computed, onMounted, ref, watch,
} from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'
import { formatCurrency, formatDate, formatMetric } from '../maintenanceUtils'

const MANAGEMENT_MODULE = 'app-management'

export default {
  name: 'ExecutedMaintenancesCard',
  components: {
    BBadge,
    BButton,
    BButtonGroup,
    BCard,
    BCardBody,
    BCol,
    BFormInput,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    vSelect,
  },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    assetId: { type: String, required: true },
  },
  setup(props) {
    const rawRecords = ref([])
    const totalRecords = ref(0)
    const isLoading = ref(false)
    const currentPage = ref(1)
    const perPage = ref(10)
    const searchQuery = ref('')
    const planFilter = ref('all') // 'all' | 'with-plan' | 'without-plan'
    const statusFilter = ref('all') // 'all' | 'finished' | 'in-progress'

    const statusOptions = computed(() => [
      { label: i18n.t('All statuses'), value: 'all' },
      { label: i18n.t('Finished'), value: 'finished' },
      { label: i18n.t('In progress'), value: 'in-progress' },
    ])

    const tableFields = [
      { key: 'planType', label: i18n.t('Maintenance Plan / Origin'), sortable: false },
      { key: 'status', label: i18n.t('Status'), sortable: false },
      { key: 'dates', label: i18n.t('Execution Date'), sortable: false },
      { key: 'metrics', label: i18n.t('Metrics (Odo / Hour)'), sortable: false },
      { key: 'value', label: i18n.t('Total Value'), sortable: false },
      { key: 'actions', label: i18n.t('Actions'), sortable: false, thClass: 'text-right', tdClass: 'text-right' },
    ]

    const hasPlan = (item) => Boolean(item.preventiveMaintenanceName || item.preventiveMaintenanceId)

    const withPlanCount = computed(() => rawRecords.value.filter(hasPlan).length)
    const withoutPlanCount = computed(() => rawRecords.value.filter(item => !hasPlan(item)).length)

    const filteredRecords = computed(() => {
      return rawRecords.value.filter(item => {
        // Plan filter
        if (planFilter.value === 'with-plan' && !hasPlan(item)) return false
        if (planFilter.value === 'without-plan' && hasPlan(item)) return false

        // Status filter
        if (statusFilter.value === 'finished' && !item.endDate) return false
        if (statusFilter.value === 'in-progress' && item.endDate) return false

        // Client search if needed
        if (searchQuery.value) {
          const q = searchQuery.value.toLowerCase().trim()
          const matchesNote = (item.note || '').toLowerCase().includes(q)
          const matchesDriver = (item.driverName || '').toLowerCase().includes(q)
          const matchesInvoice = (item.invoice || '').toLowerCase().includes(q)
          const matchesPlan = (item.preventiveMaintenanceName || '').toLowerCase().includes(q)
          const matchesCode = (item.additionalCode || '').toLowerCase().includes(q)
          if (!matchesNote && !matchesDriver && !matchesInvoice && !matchesPlan && !matchesCode) {
            return false
          }
        }

        return true
      })
    })

    const dataMeta = computed(() => {
      const count = filteredRecords.value.length
      return {
        from: perPage.value * (currentPage.value - 1) + (count ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + count,
      }
    })

    const hasDetails = (item) => Boolean(item.note || (item.items && item.items.length > 0) || item.driverName || item.invoice)

    const fetchRecords = () => {
      if (!props.assetId) return
      isLoading.value = true
      store.dispatch(`${MANAGEMENT_MODULE}/fetchMaintenances`, {
        assetId: props.assetId,
        search: '',
        page: currentPage.value,
        perPage: perPage.value,
      })
        .then(response => {
          rawRecords.value = response.data.data || []
          totalRecords.value = response.data.totalItems || rawRecords.value.length
        })
        .catch(() => {
          rawRecords.value = []
          totalRecords.value = 0
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const resetFilters = () => {
      searchQuery.value = ''
      planFilter.value = 'all'
      statusFilter.value = 'all'
    }

    watch([currentPage, perPage], () => {
      fetchRecords()
    })

    watch(() => props.assetId, () => {
      fetchRecords()
    })

    onMounted(() => {
      fetchRecords()
    })

    return {
      rawRecords,
      totalRecords,
      isLoading,
      currentPage,
      perPage,
      searchQuery,
      planFilter,
      statusFilter,
      statusOptions,
      tableFields,
      filteredRecords,
      withPlanCount,
      withoutPlanCount,
      dataMeta,
      hasPlan,
      hasDetails,
      fetchRecords,
      resetFilters,
      formatMetric,
      formatCurrency,
      formatDate,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
.executed-maintenances-card {
  .bg-light-secondary-subtle {
    background-color: #f8f8f8;
  }

  .empty-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background-color: #f3f2f7;
  }

  .plan-tag-box {
    &.with-plan {
      .tag-caption {
        font-weight: 500;
      }
    }
    &.without-plan {
      .tag-caption {
        font-weight: 400;
      }
    }
  }

  .executed-table {
    th {
      font-size: 0.82rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #6e6b7b;
      padding-top: 0.85rem;
      padding-bottom: 0.85rem;
    }

    td {
      padding-top: 0.9rem;
      padding-bottom: 0.9rem;
      vertical-align: middle;
    }
  }
}

.spin-animation {
  animation: spin 1s infinite linear;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
